import { useState } from 'react'
import { MIN_PASSWORD_LENGTH } from '@sac/contracts'
import { api } from '../lib/api'
import { Icon } from './icons'

/**
 * Self-service password change. The server destroys every session for the user and issues a
 * fresh cookie to this browser, so other devices are signed out — that is stated in the UI
 * rather than left as a surprise.
 */
export function ChangePassword({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Checked here purely for immediate feedback; the server enforces its own rules regardless.
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH
  const mismatch = confirm.length > 0 && next !== confirm
  const sameAsCurrent = next.length > 0 && next === current
  const canSubmit =
    !busy && current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && next === confirm && !sameAsCurrent

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    try {
      await api.changePassword(current, next)
      onDone()
    } catch (err) {
      const code = err instanceof Error ? err.message : 'erro'
      setError(
        code === 'invalid_current_password'
          ? 'Senha atual incorreta.'
          : code === 'invalid_body'
            ? `A nova senha precisa ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres e ser diferente da atual.`
            : 'Não foi possível alterar a senha. Tente novamente.',
      )
      setBusy(false)
    }
  }

  return (
    <>
      <div className="modal-back" onClick={onClose} />
      <div className="modal" role="dialog" aria-modal="true" aria-label="Alterar senha">
        <div className="modal-h">
          <Icon name="lock" size={15} />
          Alterar senha
          <button className="icon-btn" onClick={onClose} aria-label="Fechar">
            <Icon name="x" size={14} />
          </button>
        </div>
        <form className="modal-b" onSubmit={submit}>
          <label className="fld">
            <span>Senha atual</span>
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoFocus
            />
          </label>

          <label className="fld">
            <span>Nova senha</span>
            <input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            {tooShort && <em className="fld-e">Pelo menos {MIN_PASSWORD_LENGTH} caracteres.</em>}
            {sameAsCurrent && <em className="fld-e">Precisa ser diferente da senha atual.</em>}
          </label>

          <label className="fld">
            <span>Confirme a nova senha</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {mismatch && <em className="fld-e">As senhas não coincidem.</em>}
          </label>

          <p className="modal-note">
            Ao alterar a senha, você será desconectado nos outros dispositivos. Esta janela continua
            conectada.
          </p>

          {error && <div className="modal-err">{error}</div>}

          <div className="modal-f">
            <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button type="submit" className="btn pri" disabled={!canSubmit}>
              {busy ? 'Alterando…' : 'Alterar senha'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
